import { useMutation, useQueryClient } from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { queryKeysDeliveryNote } from '../queryKeys';

import { linkContract } from './linkContract';

type LinkContractVariables = {
  assetId: UUID;
  contractId: UUID | null;
};

/**
 * Mutation hook for linking or unlinking a contract from a delivery note.
 * Invalidates the delivery note query and its contract suggestions on success.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/link_contract
 */
export const useMutationLinkContract = (
  options?: Parameters<
    typeof useMutation<void, Error, LinkContractVariables>
  >[0],
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, LinkContractVariables>({
    mutationFn: ({ assetId, contractId }) => linkContract(assetId, contractId),
    onSuccess(_data, { assetId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysDeliveryNote.get(assetId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysDeliveryNote.getInternal(assetId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysDeliveryNote.getContractSuggestions(assetId),
      });
    },
    ...options,
  });
};
